// The pending-OAuth bridge: where a sign-in returns to.
//
// When an app (assets, VTT, …) sends a signed-out user to
// /oauth/{tenant}/authorize, the sign-in that follows can bounce the browser
// through several screens — a full-page Entra redirect, the /setup screen for a
// brand-new account, a magic-link /verify, an invitation accept. Every one of
// those ends somewhere that is not the authorize route, so the authorize
// request is stashed here first and consulted by each of those exits: if a
// request is pending, sign-in resumes it instead of dropping the user on
// /profile and stranding the app that sent them.
//
// Resuming means navigating back to the rebuilt authorize URL, so
// <oauth-authorize-view> stays the single implementation of the authorize step.
//
// Nothing here touches the DOM at import time — the decision functions are pure
// over (path, pending request) so they can be tested without a browser.

import { CONSENT_PATH } from './consents.js';

const PENDING_OAUTH_KEY = 'd3auth_oauth_params';

// Where an invitee belongs once they are signed in, when no authorize request is
// waiting on them (#101). Its own key rather than a field on the stashed
// request: readPendingOAuth requires a client id, redirect uri, and tenant slug
// before it will call an entry resumable, and a landing URL has none of those --
// folded in there it would read back as null, or worse, as a request to resume.
const INVITATION_DESTINATION_KEY = 'd3auth_invitation_destination';

// An invitation an existing account started accepting but had to sign in first
// to finish (#114). Signing in does not accept anything by itself -- an
// invitation's roles come from accepting it -- so the sign-in has to land back
// on the invitation rather than at its destination, or the invitee ends up
// signed in with the invitation still pending, which is the bug all over again.
const INVITATION_RETURN_KEY = 'd3auth_invitation_return';

// How long a stashed request stays good. It has to outlast a real sign-in — the
// user waits on an email, misses it, resends the code — so it's generous, while
// still bounded so an abandoned request can't hijack an unrelated sign-in an
// hour later. Deliberately not tied to the API's 10-minute authorization-code
// TTL: that clock starts when the code is issued, at the *end* of this one.
const PENDING_OAUTH_TTL_MS = 30 * 60 * 1000;

const PROFILE_PATH = '/profile';

// Where a screen sends someone to sign in.
//
// Not '/'. For a signed-out visitor that route renders the landing page, so a
// button labelled "Go to Sign In" left them one click short of the form, having
// to spot the sign-in control in the corner for themselves (#107). It only ever
// looked right to someone already signed in, whom the app forwards onward from
// either route.
//
// Shared with <d3-auth-app>, which routes on it: the screens that send people to
// sign in and the router that answers them read one definition, not two strings
// that can drift apart.
const SIGN_IN_PATH = '/login';

// Flows that resume themselves while the browser stays on their own route —
// <oauth-authorize-view> and <device-verify-view> both pick up from their own
// auth subscription, and the device flow needs its ?user_code left in the URL.
//
// Matched precisely rather than by a bare `/oauth/` prefix: other routes live
// under /oauth/ too (a provider round trip lands on /oauth/{provider}/complete)
// and those DO need sending onward, not leaving where they are.
const SELF_RESUMING_PATHS = [/^\/oauth\/[^/]+\/authorize/, /^\/device/];

/**
 * @typedef {object} PendingOAuth
 * @property {string} clientId
 * @property {string} redirectUri
 * @property {string} responseType
 * @property {string} scope
 * @property {string} state
 * @property {string} audience
 * @property {string[]} [resource] every RFC 8707 resource the client named; the API refuses more than one
 * @property {string} nonce
 * @property {string} codeChallenge
 * @property {string} codeChallengeMethod
 * @property {string} tenantSlug
 */

/**
 * Stash an authorize request so it survives the sign-in screens.
 * @param {PendingOAuth} params
 * @param {Storage} [storage]
 * @param {number} [now]
 */
export function savePendingOAuth(params, storage = sessionStorage, now = Date.now()) {
    storage.setItem(PENDING_OAUTH_KEY, JSON.stringify({ ...params, savedAt: now }));
}

/**
 * Stash an authorize request read straight off an authorize URL.
 *
 * <oauth-authorize-view> is the caller: it reads the request out of the address
 * bar it was loaded with, so a signed-out visitor's authorize survives the
 * sign-in screens.
 *
 * Accepts a path-with-query (`/oauth/{tenant}/authorize?...`). A URL naming no
 * client_id stashes nothing: there is no request to resume without one.
 *
 * Returns nothing on purpose. Stashing is not the same as being resumable —
 * readPendingOAuth also requires redirectUri and tenantSlug — so a boolean here
 * would read as "this will resume" while being true of entries that never can.
 * Callers that care should read the stash back.
 * @param {string} authorizeUrl
 * @param {Storage} [storage]
 */
export function savePendingOAuthFromAuthorizeUrl(authorizeUrl, storage = sessionStorage) {
    // Parsed against a throwaway base so a relative path is legal; only the
    // pathname and query are ever read back out of it.
    const parsed = new URL(authorizeUrl, 'https://auth.invalid');
    const tenantMatch = parsed.pathname.match(/^\/oauth\/([^/]+)\/authorize/);
    const query = parsed.searchParams;

    if (!query.get('client_id')) return;

    savePendingOAuth(
        {
            clientId: query.get('client_id') || '',
            redirectUri: query.get('redirect_uri') || '',
            responseType: query.get('response_type') || 'code',
            scope: query.get('scope') || '',
            state: query.get('state') || '',
            audience: query.get('audience') || '',
            // getAll, not get: RFC 8707 lets `resource` repeat, and the API
            // refuses a request naming two rather than honoring the first.
            resource: query.getAll('resource'),
            nonce: query.get('nonce') || '',
            codeChallenge: query.get('code_challenge') || '',
            codeChallengeMethod: query.get('code_challenge_method') || '',
            tenantSlug: tenantMatch ? tenantMatch[1] : '',
        },
        storage,
    );
}

/**
 * Stash where an invitation says its invitee lands, so signing in ends there.
 *
 * The invitation screen knows this and the landing page does not: by the time
 * the sign-in completes the browser has left /invitation/{id} entirely, and
 * nothing in the URL says the person arrived on an app's errand rather than
 * their own. Written on the way out, read on the way in.
 *
 * Both exits from the invitation screen need it: accepting stashes it after the
 * account is created (#74), and the "you already have an account" branch stashes
 * it before sending the invitee off to sign in (#100). Sharing one entry point
 * is what keeps the two from drifting — the second was written as a bare link
 * and quietly resumed nothing at all.
 *
 * An invitation with no redirect_uri stashes nothing, and those sign-ins end on
 * the profile page exactly as they always have. The destination is a URL the
 * inviting client registered (#111), so the app starts its own handshake when
 * the invitee arrives — which it must, since only the app can hold a PKCE
 * verifier, and by then the browser already carries an auth session.
 * The invitation id rides along as `?invitation=` (#118). An app that raised the
 * invitation holds context auth never will -- which Discord account, which
 * commission -- and nothing else spans the gap: the registered redirect_uri is
 * exact-matched against the client's list, so it is one static string and cannot
 * carry anything per-invitee, and we redeem the invitation before this redirect
 * fires, so there is no OAuth `state` in flight either. The id is the one
 * identifier both sides already agree on.
 *
 * Composed here rather than at navigate time because this is the only place
 * holding both the destination and the invitation, and it leaves the resolvers'
 * `target === invitationDestination` comparisons working on one stored string.
 *
 * Built through URL/searchParams, never string concatenation: a registered
 * redirect_uri is allowed to carry a query of its own, which a bare "?invitation="
 * would overwrite. That re-serialises the URL, so the host comes back
 * lowercased -- harmless, since hostnames are case-insensitive, and the path and
 * query are left exactly as registered.
 * @param {{id?: string, redirect_uri?: string}} invitation
 * @param {Storage} [storage]
 */
export function saveInvitationDestination(invitation, storage = sessionStorage) {
    if (!invitation.redirect_uri) return;

    storage.setItem(INVITATION_DESTINATION_KEY, invitationDestinationUrl(invitation));
}

/**
 * The registered destination with the invitation id appended.
 *
 * An unparseable redirect_uri falls back to the raw value: it was exact-matched
 * against the client's registered list before it ever got here, so failing to
 * parse means our assumption about the shape is wrong, not that the destination
 * is untrustworthy -- and sending the invitee to the registered URL without the
 * id beats stranding them.
 * @param {{id?: string, redirect_uri?: string}} invitation
 * @returns {string}
 */
function invitationDestinationUrl(invitation) {
    if (!invitation.id) return invitation.redirect_uri;

    try {
        const url = new URL(invitation.redirect_uri);
        url.searchParams.set('invitation', invitation.id);
        return url.toString();
    } catch {
        return invitation.redirect_uri;
    }
}

/**
 * Stash the invitation to come back and accept once signed in.
 * @param {string} invitationId
 * @param {Storage} [storage]
 */
export function saveInvitationReturn(invitationId, storage = sessionStorage) {
    if (!invitationId) return;

    storage.setItem(INVITATION_RETURN_KEY, invitationId);
}

/**
 * Read the invitation waiting to be accepted, or null when there isn't one.
 * @param {Storage} [storage]
 * @returns {string|null}
 */
export function readInvitationReturn(storage = sessionStorage) {
    return storage.getItem(INVITATION_RETURN_KEY);
}

/**
 * Forget the invitation waiting to be accepted.
 * @param {Storage} [storage]
 */
export function clearInvitationReturn(storage = sessionStorage) {
    storage.removeItem(INVITATION_RETURN_KEY);
}

/**
 * Read the stashed invitation destination, or null when there isn't one.
 * @param {Storage} [storage]
 * @returns {string|null}
 */
export function readInvitationDestination(storage = sessionStorage) {
    return storage.getItem(INVITATION_DESTINATION_KEY);
}

/**
 * Forget the invitation destination.
 * @param {Storage} [storage]
 */
export function clearInvitationDestination(storage = sessionStorage) {
    storage.removeItem(INVITATION_DESTINATION_KEY);
}

/**
 * Read the stashed authorize request, or null when there isn't a usable one.
 * Unparseable, incomplete, and expired entries all read as absent.
 * @param {Storage} [storage]
 * @param {number} [now]
 * @returns {PendingOAuth|null}
 */
export function readPendingOAuth(storage = sessionStorage, now = Date.now()) {
    const stored = storage.getItem(PENDING_OAUTH_KEY);
    if (!stored) return null;

    let parsed;
    try {
        parsed = JSON.parse(stored);
    } catch {
        return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.clientId || !parsed.redirectUri || !parsed.tenantSlug) return null;
    if (typeof parsed.savedAt !== 'number' || now - parsed.savedAt > PENDING_OAUTH_TTL_MS) {
        return null;
    }

    return {
        clientId: parsed.clientId,
        redirectUri: parsed.redirectUri,
        responseType: parsed.responseType || 'code',
        scope: parsed.scope || '',
        state: parsed.state || '',
        audience: parsed.audience || '',
        resource: Array.isArray(parsed.resource)
            ? parsed.resource.filter((r) => typeof r === 'string')
            : [],
        nonce: parsed.nonce || '',
        codeChallenge: parsed.codeChallenge || '',
        codeChallengeMethod: parsed.codeChallengeMethod || '',
        tenantSlug: parsed.tenantSlug,
    };
}

/**
 * Drop the stashed request — the authorize step consumed it, or it's moot.
 * @param {Storage} [storage]
 */
export function clearPendingOAuth(storage = sessionStorage) {
    storage.removeItem(PENDING_OAUTH_KEY);
}

/**
 * Rebuild the authorize URL a pending request came in on.
 * @param {PendingOAuth} params
 * @returns {string}
 */
export function pendingOAuthPath(params) {
    const query = new URLSearchParams();
    query.set('client_id', params.clientId);
    query.set('redirect_uri', params.redirectUri);
    query.set('response_type', params.responseType || 'code');
    if (params.scope) query.set('scope', params.scope);
    if (params.state) query.set('state', params.state);
    if (params.audience) query.set('audience', params.audience);
    for (const resource of params.resource || []) query.append('resource', resource);
    if (params.nonce) query.set('nonce', params.nonce);
    if (params.codeChallenge) query.set('code_challenge', params.codeChallenge);
    if (params.codeChallengeMethod) {
        query.set('code_challenge_method', params.codeChallengeMethod);
    }

    return `/oauth/${encodeURIComponent(params.tenantSlug)}/authorize?${query.toString()}`;
}

/**
 * Where a just-completed sign-in should go: null to stay put (the current flow
 * resumes itself), the acknowledge screen when a required document is
 * outstanding, the authorize URL when an app is waiting on one, the destination an invitation named when one did, else the profile page.
 *
 * A pending request outranks an invitation destination: resuming a handshake someone is
 * mid-way through is more specific than landing them at a front door, and an
 * invitation that carries both means the app asked for the handshake.
 *
 * The consent gate is checked BEFORE the self-resuming paths, and deliberately
 * so: /oauth/{tenant}/authorize is the main way a user enters an app, and a
 * gate that stepped aside for it would miss the case it exists for. The pending
 * request stays in sessionStorage across the detour, so acknowledging and
 * calling navigateAfterSignIn() again resumes the original authorize.
 *
 * The one path it does not preempt is the acknowledge screen itself, or the
 * gate would redirect to where it already is.
 * @param {string} currentPath
 * @param {PendingOAuth|null} pending
 * @param {boolean} [consentsOutstanding] whether a required document is unacknowledged
 * @param {string|null} [invitationDestination] where an invitation says its invitee lands
 * @returns {string|null}
 */
export function resolvePostSignInPath(
    currentPath,
    pending,
    consentsOutstanding = false,
    invitationDestination = null,
    invitationReturn = null,
) {
    if (currentPath.startsWith(CONSENT_PATH)) return null;
    if (consentsOutstanding) return CONSENT_PATH;

    // An invitation waiting to be accepted outranks everywhere else, and is
    // checked before the self-resuming paths for the same reason the consent
    // gate is: it is an unfinished action the user explicitly started, and
    // signing in did not finish it. Landing anywhere else leaves them signed in
    // with the invitation still pending and no roles (#114).
    //
    // A detour, not a destination — accepting calls back in here, and by then
    // this stash is cleared, so the pending request or the destination below
    // gets its turn.
    if (invitationReturn) {
        const invitationPath = `/invitation/${invitationReturn}`;
        return currentPath === invitationPath ? null : invitationPath;
    }

    if (SELF_RESUMING_PATHS.some((pattern) => pattern.test(currentPath))) {
        return null;
    }
    if (pending) return pendingOAuthPath(pending);
    if (invitationDestination) return invitationDestination;
    return PROFILE_PATH;
}

/**
 * Send a just-signed-in user on to wherever they belong. Resuming an authorize
 * request is a full page load so the authorize view boots from the URL rather
 * than from SPA state; the profile page is an in-app route, pushed and
 * announced so the router picks it up (importing the router here would drag
 * its window listeners into module load).
 * @param {Storage} [storage]
 */
export function navigateAfterSignIn(storage = sessionStorage, consentsOutstanding = false) {
    const invitationDestination = readInvitationDestination(storage);
    const invitationReturn = readInvitationReturn(storage);
    const target = resolvePostSignInPath(
        window.location.pathname,
        readPendingOAuth(storage),
        consentsOutstanding,
        invitationDestination,
        invitationReturn,
    );
    if (target === null) return;

    // Consumed on arrival: the invitation page takes it from here, and leaving
    // it set would bounce every later sign-in in this tab back to it.
    if (invitationReturn && target === `/invitation/${invitationReturn}`) {
        clearInvitationReturn(storage);
    }

    // Consumed only when it is actually where we are going. Left alone on the
    // way to the acknowledge screen, which is a detour the invitee comes back
    // from -- and cleared once used, so an abandoned invitation cannot capture
    // an unrelated sign-in later in the same tab.
    if (target === invitationDestination) clearInvitationDestination(storage);

    goTo(target);
}

/**
 * Where a user leaving the acknowledge screen belongs: the authorize URL when
 * an app is waiting on one, else the profile page.
 *
 * Leaving the gate is a different question from arriving at it, which is why
 * it gets its own resolver rather than a flag on resolvePostSignInPath. By the
 * time this is asked the answer depends on one thing only — whether a request
 * is pending. The current path is /consent by definition, nothing is
 * outstanding any more, and /consent resumes nothing by itself. Asking
 * resolvePostSignInPath instead gets `null` from its own arrival guard, which
 * is what stranded the user on the acknowledge screen (#53).
 *
 * Never returns null: the whole point of the gate is that it lets go.
 * @param {PendingOAuth|null} pending
 * @param {string|null} [invitationDestination] where an invitation says its invitee lands
 * @returns {string}
 */
export function resolvePostConsentPath(pending, invitationDestination = null) {
    if (pending) return pendingOAuthPath(pending);
    if (invitationDestination) return invitationDestination;
    return PROFILE_PATH;
}

/**
 * Release a user who has acknowledged everything they owe. The stashed
 * authorize request survived the detour — the gate reads it, never consumes
 * it — so an app that sent the user here gets them back.
 * @param {Storage} [storage]
 */
export function navigateAfterConsent(storage = sessionStorage) {
    const invitationDestination = readInvitationDestination(storage);
    const target = resolvePostConsentPath(readPendingOAuth(storage), invitationDestination);

    if (target === invitationDestination) clearInvitationDestination(storage);

    goTo(target);
}

// How we get there, shared by both exits above. In-app routes are pushed and
// announced so the router picks them up; anything else is a full page load.
function goTo(target) {
    if (target === PROFILE_PATH || target === CONSENT_PATH) {
        window.history.pushState({}, '', target);
        window.dispatchEvent(new PopStateEvent('popstate'));
        return;
    }

    window.location.assign(target);
}

export {
    INVITATION_DESTINATION_KEY,
    INVITATION_RETURN_KEY,
    PENDING_OAUTH_KEY,
    PENDING_OAUTH_TTL_MS,
    PROFILE_PATH,
    SIGN_IN_PATH,
};
